/**
 * @fileoverview Dashboard data context definition and types.
 * @module shared/context/DashboardDataContext
 */

import { createContext } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import {
  UnifiedStatisticsResponseDTO,
  Variant,
  StatisticsDimensionGroup,
} from '@/lib/statisticsService'

/**
 * Context type definition for dashboard data state.
 */
export interface DashboardDataContextType {
  /** Sales statistics with TIME dimension */
  salesTimeQuery: UseQueryResult<UnifiedStatisticsResponseDTO, Error>
  /** Sales statistics with COUNTRY+PRODUCT dimensions */
  salesProductQuery: UseQueryResult<UnifiedStatisticsResponseDTO, Error>
  /** Sales statistics with PRODUCT dimension only (no country breakdown) */
  salesProductOnlyQuery: UseQueryResult<UnifiedStatisticsResponseDTO, Error>
  /** Variants list query result */
  variantsQuery: UseQueryResult<Variant[], Error>
  /** Combined loading state */
  isLoading: boolean
  /** Combined error state */
  error: string | null
  /** Collapsed product group metadata keyed by group slug */
  productGroups: Map<string, StatisticsDimensionGroup>
  /** Prefetch the country+product query (for lazy loading) */
  prefetchCountryProductQuery: () => void

  // Legacy query results - aliases for smooth transition
  /** @deprecated Use salesTimeQuery instead */
  ecommerceQuery: UseQueryResult<UnifiedStatisticsResponseDTO, Error>
  /** @deprecated Use salesProductQuery instead */
  productsQuery: UseQueryResult<UnifiedStatisticsResponseDTO, Error>
  /** @deprecated Use variantsQuery instead */
  skusQuery: UseQueryResult<Variant[], Error>
}

/**
 * Dashboard data context for managing centralized data fetching.
 */
export const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined)
